import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useNowMinute } from '../../lib/hooks.js';
import { Avatar, Stars, Loading, Empty, useToast, Field, KeyValue } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { currency, formatDayLabel, timeRange, todayISO, formatTime, formatDate } from '../../lib/format.js';
import ServiceIcon from '../../components/ServiceIcon.jsx';
import VisitLocation from '../../components/VisitLocation.jsx';

function slotStarts(window) {
  const out = [];
  const [sh, sm] = window.start_time.split(':').map(Number);
  const [eh] = window.end_time.split(':').map(Number);
  for (let h = sh; h < eh; h++) out.push(`${String(h).padStart(2, '0')}:${String(sm).padStart(2, '0')}`);
  return out;
}
const addHours = (hhmm, n) => {
  const [h, m] = hhmm.split(':').map(Number);
  return `${String(Math.min(h + n, 23)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const STEPS = ['Patient', 'Service', 'Schedule', 'Details', 'Confirm'];

/**
 * Admin-assisted booking: an administrator books a verified nurse on behalf of a
 * patient chosen from the platform's patient list. It mirrors the patient
 * wizard but adds patient selection as the first step and posts to the
 * admin-only endpoint that records the chosen patient as the booker.
 */
export default function AdminBook() {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();

  const [nurse, setNurse] = useState(null);
  const [patients, setPatients] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);

  const [patientId, setPatientId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [date, setDate] = useState('');
  const [start, setStart] = useState('');
  const [duration, setDuration] = useState(2);
  const [location, setLocation] = useState({ mode: 'other', address: '', city: '', lat: null, lng: null });
  const [notes, setNotes] = useState('');
  const now = useNowMinute(); // ticks every minute so expired slots drop live

  const patchLocation = (patch) => setLocation((l) => ({ ...l, ...patch }));
  const { address, city } = location;

  useEffect(() => {
    api.get(`/api/nurses/${id}`).then(setNurse).catch((e) => setError(e.message));
    api
      .get('/api/admin/users', { role: 'patient' })
      .then((rows) => setPatients(rows.filter((u) => !u.isBlocked)))
      .catch(() => setPatients([]));
  }, [id]);

  const patient = patients.find((p) => String(p.id) === String(patientId));

  // Prefill from the chosen patient's saved address; switching patient replaces
  // it, including any pin dropped for the previous one.
  useEffect(() => {
    if (!patient) return;
    setLocation(
      patient.address
        ? { mode: 'saved', address: patient.address, city: patient.city || '', lat: null, lng: null }
        : { mode: 'other', address: '', city: patient.city || '', lat: null, lng: null }
    );
  }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const { days, windows, freeStarts, allExpired } = useMemo(() => {
    if (!nurse) return { days: [], windows: [], freeStarts: [], allExpired: false };
    const taken = (d, s, e) => nurse.bookedSlots.some((b) => b.date === d && b.start_time < e && s < b.end_time);
    const grouped = {};
    for (const slot of nurse.availability) {
      if (slot.date < todayISO()) continue;
      (grouped[slot.date] ||= []).push(slot);
    }
    const daysList = Object.keys(grouped).sort().slice(0, 21);
    const win = date ? grouped[date] || [] : [];
    const isToday = date === todayISO();

    let generated = 0;
    const free = [];
    for (const w of win) {
      for (const s of slotStarts(w)) {
        const e = addHours(s, duration);
        generated += 1;
        // The API rejects a lapsed start time for admin-booked visits too, so
        // hide them here rather than let the wizard fail at the last step.
        if (isToday && s <= now) continue;
        free.push({ start: s, end: e, disabled: e > w.end_time || taken(date, s, e) });
      }
    }
    return {
      days: daysList,
      windows: win,
      freeStarts: free,
      allExpired: isToday && generated > 0 && free.length === 0,
    };
  }, [nurse, date, duration, now]);

  // Drop a chosen start once the clock ticks past it.
  useEffect(() => {
    if (start && date === todayISO() && start <= now) setStart('');
  }, [now, date, start]);

  const service = nurse?.services.find((s) => String(s.id) === String(serviceId));
  const end = start ? addHours(start, duration) : '';
  const amount = nurse ? duration * nurse.hourly_rate : 0;

  const locationReady = !!address.trim() && (location.mode !== 'other' || location.lat != null);
  const stepValid = [!!patient, !!service, !!(date && start), locationReady, true];
  const summary = [
    patient ? patient.name : 'Not chosen',
    service ? service.name : 'Not chosen',
    date && start ? `${formatDayLabel(date)}, ${formatTime(start)}` : 'Not chosen',
    address.trim() ? address.split('\n')[0].slice(0, 24) : 'Not entered',
    date && start ? currency(amount) : '—',
  ];

  const goTo = (next) => {
    if (next <= step || stepValid.slice(0, next).every(Boolean)) {
      setStep(next);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const booking = await api.post('/api/admin/bookings', {
        patientId: Number(patientId),
        nurseId: Number(id),
        serviceId: Number(serviceId),
        date,
        startTime: start,
        endTime: end,
        address: address.trim(),
        city,
        notes,
        lat: location.lat,
        lng: location.lng,
      });
      toast.success(`Booked ${nurse.name} for ${patient.name}.`);
      navigate(`/bookings/${booking.id}`, { replace: true });
    } catch (err) {
      toast.error(err);
      if (err.status === 409) {
        api.get(`/api/nurses/${id}`).then(setNurse).catch(() => {});
        setStart('');
        setStep(2);
      }
      setBusy(false);
    }
  };

  if (error)
    return (
      <Empty art="not-found" title="Cannot book this nurse">
        {error}
      </Empty>
    );
  if (!nurse) return <Loading rows={2} />;

  return (
    <>
      <Link to="/admin/users" className="back-link">
        <Icon name="arrow-left" />
        Back to users
      </Link>

      <div className="page-head">
        <Avatar name={nurse.name} avatar={nurse.avatar} />
        <div className="grow">
          <h1>Book {nurse.name}</h1>
          <p className="row-tight" style={{ flexWrap: 'wrap' }}>
            <span className="pill pill-accent">Admin-assisted</span>
            <span>{currency(nurse.hourly_rate)}/hr</span>
            <Stars value={nurse.rating_avg} count={nurse.rating_count} />
          </p>
        </div>
      </div>

      <nav className="wizard-rail" aria-label="Booking steps">
        {STEPS.map((name, i) => (
          <button
            key={name}
            type="button"
            data-state={i === step ? 'current' : i < step && stepValid[i] ? 'done' : 'todo'}
            disabled={i > step && !stepValid.slice(0, i).every(Boolean)}
            onClick={() => goTo(i)}
            aria-current={i === step ? 'step' : undefined}
          >
            <span className="wizard-bar" />
            <span className="wizard-name">
              {i + 1}. {name}
            </span>
            <span className="wizard-value">{summary[i]}</span>
          </button>
        ))}
      </nav>

      {/* With the sidebar gone on the confirm step the column would stretch the
          full page width, so it drops to a single readable measure instead. */}
      <div className={step < 4 ? 'detail-layout' : 'wizard-confirm'}>
        <div>
          <div className="step-panel" key={step}>
            {step === 0 && (
              <section className="card">
                <div className="card-header">
                  <h2>Which patient is this for?</h2>
                </div>
                <div className="card-body">
                  {patients.length === 0 ? (
                    <Empty art="no-results" title="No patients yet">
                      Bookings can be made once a patient has registered.
                    </Empty>
                  ) : (
                    <>
                      <Field label="Patient" id="a-patient" hint="Search the platform's registered patients.">
                        <select id="a-patient" value={patientId} onChange={(e) => setPatientId(e.target.value)}>
                          <option value="">Select a patient…</option>
                          {patients.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} — {p.email}
                            </option>
                          ))}
                        </select>
                      </Field>
                      {patient && (
                        <div className="banner banner-info" style={{ marginBottom: 0 }}>
                          <Icon name="info" />
                          <span className="grow">
                            Booking for <strong>{patient.name}</strong>
                            {patient.phone ? ` · ${patient.phone}` : ''}
                            {patient.city ? ` · ${patient.city}` : ''}. They will be notified.
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </section>
            )}

            {step === 1 && (
              <section className="card">
                <div className="card-header">
                  <h2>What care is needed?</h2>
                </div>
                <div className="card-body">
                  <div className="grid grid-3">
                    {nurse.services.map((s, i) => (
                      <button
                        key={s.id}
                        type="button"
                        className="service-tile anim-rise"
                        style={{ '--i': i }}
                        data-selected={String(s.id) === String(serviceId) ? 'true' : 'false'}
                        onClick={() => {
                          setServiceId(String(s.id));
                          goTo(2);
                        }}
                      >
                        <span className="ico" aria-hidden="true">
                          <ServiceIcon service={s} size={26} />
                        </span>
                        <span className="nm">{s.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {step === 2 && (
              <section className="card">
                <div className="card-header">
                  <h2>When should the nurse come?</h2>
                </div>
                <div className="card-body">
                  {days.length === 0 ? (
                    <Empty art="schedule" title="No open slots">
                      This nurse has not published availability yet.
                    </Empty>
                  ) : (
                    <>
                      <span className="label mb-1">Pick a day</span>
                      <div className="day-strip">
                        {days.map((d) => (
                          <button
                            key={d}
                            type="button"
                            className="day-pill"
                            aria-pressed={date === d}
                            onClick={() => {
                              setDate(d);
                              setStart('');
                            }}
                          >
                            <span>{formatDayLabel(d).slice(0, 3)}</span>
                            <b>{Number(d.slice(8, 10))}</b>
                            <span>{new Date(`${d}T12:00:00`).toLocaleDateString('en-IN', { month: 'short' })}</span>
                          </button>
                        ))}
                      </div>

                      <Field label="How long?">
                        <div className="chips">
                          {[1, 2, 3, 4, 6, 8, 12].map((h) => (
                            <button
                              key={h}
                              type="button"
                              className="chip"
                              aria-pressed={duration === h}
                              onClick={() => {
                                setDuration(h);
                                setStart('');
                              }}
                            >
                              {h} hr{h > 1 ? 's' : ''} · {currency(h * nurse.hourly_rate)}
                            </button>
                          ))}
                        </div>
                      </Field>

                      {date && (
                        <>
                          <span className="label">Start time</span>
                          {windows.length > 0 && (
                            <p className="tiny dim" style={{ margin: '4px 0 10px' }}>
                              Working {windows.map((w) => timeRange(w.start_time, w.end_time)).join(' and ')}.
                            </p>
                          )}
                          {allExpired ? (
                            <div className="banner banner-warning">
                              <Icon name="clock" />
                              <span className="grow">
                                No available time slots remaining for today. Please choose another date.
                              </span>
                            </div>
                          ) : freeStarts.every((s) => s.disabled) ? (
                            <div className="banner banner-warning">
                              <Icon name="warning" />
                              <span className="grow">No {duration}-hour slot fits. Try a shorter duration.</span>
                            </div>
                          ) : (
                            <div className="slot-grid">
                              {freeStarts.map((s) => (
                                <button
                                  key={s.start}
                                  type="button"
                                  className="slot"
                                  aria-pressed={start === s.start}
                                  disabled={s.disabled}
                                  onClick={() => setStart(s.start)}
                                >
                                  {formatTime(s.start)}
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </section>
            )}

            {step === 3 && (
              <section className="card">
                <div className="card-header">
                  <h2>Visit address</h2>
                </div>
                <div className="card-body">
                  <VisitLocation
                    value={location}
                    onChange={patchLocation}
                    saved={{ address: patient?.address, city: patient?.city }}
                    savedLabel="Patient's saved address"
                    allowCurrent={false}
                  />
                  <Field label="Notes for the nurse" id="a-notes" optional>
                    <textarea
                      id="a-notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Conditions, mobility needs, access instructions."
                    />
                  </Field>
                </div>
              </section>
            )}

            {/* The running sidebar summary is hidden on this step and folded in
                here, so the final screen states the booking once. */}
            {step === 4 && (
              <section className="card">
                <div className="card-header">
                  <h2 className="grow">Review the booking</h2>
                  <Icon name="clipboard" size={16} className="dim" />
                </div>

                <div className="card-body" style={{ paddingBottom: 0 }}>
                  <div className="row-tight">
                    <Avatar name={nurse.name} avatar={nurse.avatar} />
                    <div className="grow">
                      <div className="t-title truncate">{nurse.name}</div>
                      <div className="tiny dim">
                        {currency(nurse.hourly_rate)}/hr · {nurse.city}
                      </div>
                    </div>
                    <Stars value={nurse.rating_avg} count={nurse.rating_count} />
                  </div>
                  <hr className="divider" />
                </div>

                <div className="card-body" style={{ paddingTop: 0 }}>
                  <dl style={{ margin: 0 }}>
                    <KeyValue label="Patient" action={<EditLink onClick={() => goTo(0)} />}>
                      {patient?.name}
                    </KeyValue>
                    <KeyValue label="Service" action={<EditLink onClick={() => goTo(1)} />}>
                      {service?.name}
                    </KeyValue>
                    <KeyValue label="Date" action={<EditLink onClick={() => goTo(2)} />}>
                      {formatDayLabel(date)}, {formatDate(date)}
                    </KeyValue>
                    <KeyValue label="Time" action={<EditLink onClick={() => goTo(2)} />}>
                      {timeRange(start, end)} ({duration} hr{duration > 1 ? 's' : ''})
                    </KeyValue>
                    <KeyValue label="Address" action={<EditLink onClick={() => goTo(3)} />}>
                      {address}
                      {city ? `, ${city}` : ''}
                    </KeyValue>
                    {notes && (
                      <KeyValue label="Notes" action={<EditLink onClick={() => goTo(3)} />}>
                        {notes}
                      </KeyValue>
                    )}
                  </dl>

                  <hr className="divider" />

                  <dl style={{ margin: 0 }}>
                    <KeyValue label={`${currency(nurse.hourly_rate)} × ${duration} hour${duration > 1 ? 's' : ''}`}>
                      {currency(amount)}
                    </KeyValue>
                    <KeyValue label="Payment">Cash, after the visit</KeyValue>
                  </dl>

                  <div className="summary-total">
                    <span>Total payable</span>
                    <span className="rate">{currency(amount)}</span>
                  </div>
                </div>
              </section>
            )}

            <div className="wizard-actions">
              <button className="btn" onClick={() => goTo(step - 1)} disabled={step === 0 || busy}>
                <Icon name="arrow-left" />
                <span className="sr-only">Back</span>
              </button>
              <div className="grow">
                {!stepValid[step] && step < 4 && (
                  <span className="tiny dim">
                    {
                      [
                        'Select a patient to continue.',
                        'Choose a service.',
                        'Pick a day and start time.',
                        location.mode === 'other' && location.lat == null
                          ? 'Confirm the spot on the map so the nurse can find the place.'
                          : 'Enter the visit address.',
                      ][step]
                    }
                  </span>
                )}
              </div>
              {step < 4 ? (
                <button className="btn btn-primary btn-lg" disabled={!stepValid[step]} onClick={() => goTo(step + 1)}>
                  Continue <Icon name="arrow-right" />
                </button>
              ) : (
                <button className="btn btn-primary btn-lg" disabled={busy} onClick={submit}>
                  {busy ? <span className="spinner" /> : `Confirm booking · ${currency(amount)}`}
                </button>
              )}
            </div>
          </div>
        </div>

        {step < 4 && (
        <aside>
          <section className="card sticky-aside">
            <div className="card-header">
              <h3 className="grow">Summary</h3>
              <Icon name="clipboard" size={16} className="dim" />
            </div>
            <div className="card-body">
              <dl style={{ margin: 0 }}>
                <KeyValue label="Patient">{patient?.name || '—'}</KeyValue>
                <KeyValue label="Nurse">{nurse.name}</KeyValue>
                <KeyValue label="Service">{service ? service.name : '—'}</KeyValue>
                <KeyValue label="Date">{date ? formatDayLabel(date) : '—'}</KeyValue>
                <KeyValue label="Time">{start ? timeRange(start, end) : '—'}</KeyValue>
              </dl>
              <div className="summary-total">
                <span>Total</span>
                <span className="rate">{currency(amount)}</span>
              </div>
              <p className="tiny dim mt-2">Paid in cash to the nurse after the visit.</p>
            </div>
          </section>
        </aside>
        )}
      </div>
    </>
  );
}

/** Inline "edit" affordance that jumps the wizard back to a step. */
function EditLink({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: 'none',
        background: 'none',
        color: 'var(--accent)',
        cursor: 'pointer',
        padding: '0 0 0 8px',
        fontWeight: 600,
        fontSize: 'var(--fs-small)',
      }}
    >
      Edit
    </button>
  );
}
